import { store } from '@/store'
import { officeApi } from '@/store/services'
import { toOptions } from '@/utils'
import { useEffect, useState } from 'react'
import { createSearchParams } from 'react-router-dom'
import { AsyncPaginate } from 'react-select-async-paginate'
import { SelectWrapper } from '../wrapper'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

export const OfficeSelector = ({
  value,
  onChange,
  isMulti = false,
  isClearable = false,
  ids = null,
  label = 'Dependencia',
  placeholder,
  className
}) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState(null)

  const officeLoadOptions = async (search, loadedOptions, { page }) => {
    if (Array.isArray(ids) && ids.length === 0) {
      return {
        options: [],
        hasMore: false,
        additional: { page: page + 1 }
      }
    }

    const searchParams = createSearchParams({
      page,
      q: search,
      'order[field]': 'name',
      'order[sort]': 'asc'
    })

    if (Array.isArray(ids)) {
      ids.forEach((id) => searchParams.append('id[]', String(id)))
    }

    const { offices, meta } = await store
      .dispatch(officeApi.endpoints.getOffices.initiate(`?${searchParams.toString()}`, { subscribe: true }))
      .unwrap()

    const officeOptions = toOptions({ data: offices })
    const hasMore = page < meta.pagination.total_pages

    return {
      options: officeOptions,
      hasMore,
      additional: { page: page + 1 }
    }
  }

  const getCurrentOffice = async (id) => {
    if (!id) return
    const { office = {} } = await store.dispatch(officeApi.endpoints.getOffice.initiate(id)).unwrap()
    setSelectedOption({ value: office.id, label: office.name })
  }

  useEffect(() => {
    if (value) {
      getCurrentOffice(value)
    } else {
      setSelectedOption(null)
    }
  }, [value])

  const handleOfficeChange = (option) => {
    setSelectedOption(option)
    onChange(option)
  }

  return (
    <SelectWrapper label={label} className={className}>
      {(styles) => (
        <AsyncPaginate
          isMulti={isMulti}
          isClearable={isClearable}
          loadingMessage={() => t('select.loading')}
          value={selectedOption}
          debounceTimeout={600}
          placeholder={placeholder ?? t('select.one')}
          onChange={handleOfficeChange}
          loadOptions={officeLoadOptions}
          styles={styles}
          menuPortalTarget={document.body}
          additional={{ page: 1 }}
        />
      )}
    </SelectWrapper>
  )
}

OfficeSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  ids: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string
}
